import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, MoreHorizontal, Edit2, BarChart3, Copy, EyeOff, Eye, Trash2, Power, MessageCircle, Mail, Layers, Pencil } from "lucide-react";
import { api } from "../../lib/api";
import { StatusPill, SheetHealthDot, BrandLogo, EmptyState, Skeleton, ClearFiltersButton } from "../../components/common";
import { formatINRAbbrev } from "../../lib/format";
import { toast } from "sonner";
import CampaignFormModal from "./CampaignFormModal";
import CommentsDrawer from "../brand/CommentsDrawer";
import NotifyBrandModal from "../../components/NotifyBrandModal";
import { CampaignGroupsPanel } from "./CampaignGroupsPanel";
import { CampaignBulkEditDialog } from "./CampaignBulkEditDialog";

const STATUS_OPTS = ["Active", "Pending", "Completed", "Expired"];
const BILLING_OPTS = ["Pending", "Paid", "Overdue"];

export default function CampaignsListPage() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [brands, setBrands] = useState([]);
  const [teams, setTeams] = useState([]);
  const [groups, setGroups] = useState([]);
  // View mode toggle: "campaigns" is the table (default), "groups" is the
  // internal groups management panel. Groups are admin-only and never touch
  // the brand portal.
  const [view, setView] = useState("campaigns");
  // Selected campaign IDs for ad-hoc bulk actions. Uses a Set for O(1) toggle.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkEdit, setBulkEdit] = useState(null); // null | { ids: string[], contextLabel?: string }
  // Hydrate filters once from the URL on mount (lazy initial state — keeps first paint in sync
  // with the address bar without needing a setState-in-effect dance).
  const [filters, setFiltersRaw] = useState(() => {
    const base = { q: "", brand_id: "", team_id: "", status: "", billing: "", month: "" };
    if (typeof window === "undefined") return base;
    const params = new URLSearchParams(window.location.search);
    Object.keys(base).forEach((k) => { const v = params.get(k); if (v) base[k] = v; });
    return base;
  });
  const [modal, setModal] = useState(null); // null | "new" | campaign object
  const [openMenu, setOpenMenu] = useState(null);
  const [commentsFor, setCommentsFor] = useState(null);
  const [notifyFor, setNotifyFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, b, t, g] = await Promise.all([
        api.get("/campaigns"),
        api.get("/brands"),
        api.get("/teams"),
        api.get("/campaign-groups"),
      ]);
      setCampaigns(c.data.campaigns);
      setBrands(b.data.brands);
      setTeams(t.data.teams);
      setGroups(g.data.groups || []);
    } catch (e) {
      toast.error("Failed to load campaigns");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Reflect filter changes back into the URL bar (history.replaceState — no re-render).
  // Wrap setFilters so we update both state and URL together, avoiding a setState-in-effect.
  const setFilters = useCallback((next) => {
    setFiltersRaw((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      const params = new URLSearchParams();
      Object.entries(value).forEach(([k, v]) => { if (v) params.set(k, v); });
      const qs = params.toString();
      window.history.replaceState(null, "", qs ? `?${qs}` : window.location.pathname);
      return value;
    });
  }, []);

  const filtered = useMemo(() => {
    return campaigns.filter((c) => {
      if (filters.q && !(`${c.campaign_name} ${c.campaign_id}`.toLowerCase().includes(filters.q.toLowerCase()))) return false;
      if (filters.brand_id && c.brand_id !== filters.brand_id) return false;
      if (filters.team_id && !(c.team_ids || []).includes(filters.team_id)) return false;
      if (filters.status && c.status !== filters.status) return false;
      if (filters.billing && (c.tracker_values?.billing_status !== filters.billing)) return false;
      if (filters.month && (c.tracker_values?.month || "").toLowerCase() !== filters.month.toLowerCase()) return false;
      return true;
    });
  }, [campaigns, filters]);

  const monthOpts = useMemo(() => {
    const m = new Set();
    campaigns.forEach((c) => { if (c.tracker_values?.month) m.add(c.tracker_values.month); });
    return [...m];
  }, [campaigns]);

  const onDuplicate = async (id) => {
    try { await api.post(`/campaigns/${id}/duplicate`); toast.success("Campaign duplicated"); load(); }
    catch { toast.error("Duplicate failed"); }
  };

  const onToggleVisibility = async (c) => {
    try {
      await api.put(`/campaigns/${c.id}`, { visible_to_brand: !c.visible_to_brand });
      toast.success(`Visibility ${!c.visible_to_brand ? "enabled" : "disabled"}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const onDeactivate = async (c) => {
    const next = c.status === "Active" ? "Completed" : "Active";
    try {
      await api.put(`/campaigns/${c.id}`, { status: next });
      toast.success(`Status set to ${next}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this campaign? This cannot be undone.")) return;
    try { await api.delete(`/campaigns/${id}`); toast.success("Campaign deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-campaigns-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Campaigns</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Manage every campaign across all brands.</p>
        </div>
        <button onClick={() => setModal("new")} className="wl-btn-primary inline-flex items-center gap-2" data-testid="create-campaign-btn">
          <Plus size={16} /> Create Campaign
        </button>
      </div>

      {/* View mode toggle: campaigns table vs internal groups panel. Groups
          are hidden from brand users end-to-end. */}
      <div className="mb-4 flex items-center gap-1 border-b border-[var(--border-default)]">
        <button
          onClick={() => setView("campaigns")}
          className={`text-sm px-4 py-2.5 -mb-px border-b-2 ${view === "campaigns" ? "border-[var(--brand)] font-semibold text-[var(--text-primary)]" : "border-transparent text-[var(--text-secondary)]"}`}
          data-testid="view-tab-campaigns"
        >All campaigns</button>
        <button
          onClick={() => setView("groups")}
          className={`text-sm px-4 py-2.5 -mb-px border-b-2 inline-flex items-center gap-1.5 ${view === "groups" ? "border-[var(--brand)] font-semibold text-[var(--text-primary)]" : "border-transparent text-[var(--text-secondary)]"}`}
          data-testid="view-tab-groups"
        ><Layers size={13} /> Groups <span className="text-[10px] text-[var(--text-secondary)]">({groups.length})</span></button>
      </div>

      {view === "groups" ? (
        <CampaignGroupsPanel
          campaigns={campaigns}
          onRequestBulkEdit={(ids, ctx) => setBulkEdit({ ids, contextLabel: ctx })}
        />
      ) : (
      <>
      {/* Selection action bar — shows when the admin has ticked one or more
          rows. Provides bulk actions without leaving the campaigns table. */}
      {selected.size > 0 && (
        <div className="wl-card-solid p-3 mb-3 flex items-center gap-3 border-l-4" style={{ borderLeftColor: "var(--brand)" }} data-testid="selection-action-bar">
          <div className="text-sm">
            <strong data-testid="selection-count">{selected.size}</strong> selected
          </div>
          <div className="flex-1" />
          <button
            onClick={() => setBulkEdit({ ids: [...selected], contextLabel: "selection" })}
            className="wl-btn-secondary text-sm inline-flex items-center gap-1.5"
            data-testid="bulk-edit-selection-btn"
          ><Pencil size={13} /> Bulk edit</button>
          <select
            className="wl-input wl-input-sm w-auto"
            defaultValue=""
            onChange={async (e) => {
              const gid = e.target.value;
              e.target.value = "";
              if (!gid) return;
              try {
                await api.post(`/campaign-groups/${gid}/assign`, { campaign_ids: [...selected] });
                toast.success(`Assigned ${selected.size} to group`);
                setSelected(new Set());
                load();
              } catch { toast.error("Assign failed"); }
            }}
            data-testid="assign-group-select"
            title={groups.length === 0 ? "Create a group first (Groups tab)" : "Assign to a group"}
            disabled={groups.length === 0}
          >
            <option value="" disabled>{groups.length === 0 ? "No groups yet" : "Assign to group…"}</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <button
            onClick={() => setSelected(new Set())}
            className="text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            data-testid="clear-selection-btn"
          >Clear</button>
        </div>
      )}

      {/* Filters */}
      <div className="wl-card-solid p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="filters-bar">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
          <input className="wl-input pl-9" placeholder="Search campaign name or ID"
            value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            data-testid="filter-search" />
        </div>
        <Select value={filters.brand_id} onChange={(v) => setFilters({ ...filters, brand_id: v })} options={[{ value: "", label: "All brands" }, ...brands.map(b => ({ value: b.id, label: b.name }))]} testid="filter-brand" />
        <Select value={filters.team_id} onChange={(v) => setFilters({ ...filters, team_id: v })} options={[{ value: "", label: "All teams" }, ...teams.filter(t => !filters.brand_id || t.brand_id === filters.brand_id).map(t => ({ value: t.id, label: t.name }))]} testid="filter-team" />
        <Select value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} options={[{ value: "", label: "Status" }, ...STATUS_OPTS.map(s => ({ value: s, label: s }))]} testid="filter-status" />
        <Select value={filters.billing} onChange={(v) => setFilters({ ...filters, billing: v })} options={[{ value: "", label: "Billing" }, ...BILLING_OPTS.map(s => ({ value: s, label: s }))]} testid="filter-billing" />
        <Select value={filters.month} onChange={(v) => setFilters({ ...filters, month: v })} options={[{ value: "", label: "Month" }, ...monthOpts.map(s => ({ value: s, label: s }))]} testid="filter-month" />
        <ClearFiltersButton filters={filters} defaults={{ q: "", brand_id: "", team_id: "", status: "", billing: "", month: "" }} onClear={setFilters} testid="campaigns-clear-filters" />
        <div className="ml-auto text-sm text-[var(--text-secondary)]" data-testid="filter-count">{filtered.length} of {campaigns.length}</div>
      </div>

      {/* Table */}
      <div className="wl-card-solid overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : filtered.length === 0 ? (
          <EmptyState title={campaigns.length === 0 ? "No campaigns yet" : "No results"}
            subtitle={campaigns.length === 0 ? "Create your first campaign to get started." : "Try adjusting filters."} />
        ) : (
          <div className="overflow-x-auto">
            <table className="wl-table" data-testid="campaigns-table">
              <thead>
                <tr>
                  <th className="w-8">
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && filtered.every((c) => selected.has(c.id))}
                      onChange={(e) => {
                        const next = new Set(selected);
                        if (e.target.checked) filtered.forEach((c) => next.add(c.id));
                        else filtered.forEach((c) => next.delete(c.id));
                        setSelected(next);
                      }}
                      data-testid="select-all-checkbox"
                      title="Select all visible"
                    />
                  </th>
                  <th>Brand</th><th>Teams</th><th>Month</th><th>Campaign</th><th>ID</th>
                  <th>Deliverable</th><th>Status</th><th>Date</th><th>Budget</th><th>Billing</th>
                  <th>DB IDs</th><th>VR</th>
                  <th>Sheet</th><th>Cmts</th><th className="text-right pr-6">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} data-testid={`campaign-row-${c.campaign_id}`} className={selected.has(c.id) ? "bg-[var(--bg-surface-warm)]" : ""}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(c.id)}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) next.add(c.id); else next.delete(c.id);
                          setSelected(next);
                        }}
                        data-testid={`select-campaign-${c.campaign_id}`}
                        aria-label={`Select ${c.campaign_name || c.campaign_id}`}
                      />
                    </td>
                    <td>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="flex items-center -space-x-2 shrink-0">
                          <BrandLogo name={c.brand?.name} logoUrl={c.brand?.logo_url} size={28} />
                          {(c.partner_brands || []).slice(0, 2).map((pb) => (
                            <div key={pb.id} className="ring-2 ring-white rounded-full" title={pb.name} data-testid={`partner-brand-logo-${c.campaign_id}-${pb.id}`}>
                              <BrandLogo name={pb.name} logoUrl={pb.logo_url} size={22} />
                            </div>
                          ))}
                          {(c.partner_brands || []).length > 2 && (
                            <span className="ring-2 ring-white rounded-full bg-[var(--bg-page)] text-[10px] font-semibold w-[22px] h-[22px] inline-flex items-center justify-center text-[var(--text-secondary)]">
                              +{c.partner_brands.length - 2}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{c.brand?.name || "—"}</div>
                          {(c.partner_brands || []).length > 0 && (
                            <div className="text-[10px] text-[var(--text-secondary)] truncate" title={c.partner_brands.map((b) => b.name).join(", ")}>
                              × {c.partner_brands.map((b) => b.name).join(", ")}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {(c.teams || []).slice(0, 2).map((t) => <span key={t.id} className="wl-pill wl-pill-neutral">{t.name}</span>)}
                        {c.teams?.length > 2 && <span className="text-xs text-[var(--text-secondary)]">+{c.teams.length - 2}</span>}
                      </div>
                    </td>
                    <td className="text-[var(--text-secondary)]">{c.tracker_values?.month || "—"}</td>
                    <td className="font-medium">{c.campaign_name}</td>
                    <td className="text-[var(--text-secondary)] text-xs">{c.campaign_id}</td>
                    <td>{c.tracker_values?.deliverable || "—"}</td>
                    <td><StatusPill value={c.status} /></td>
                    <td className="text-[var(--text-secondary)]">{c.tracker_values?.date || "—"}</td>
                    <td>{formatINRAbbrev(c.tracker_values?.budget)}</td>
                    <td><StatusPill value={c.tracker_values?.billing_status} type="billing" /></td>
                    <td>
                      {(c.db_ids && c.db_ids.length > 0) ? (
                        <span
                          className="text-[11px] font-mono text-[var(--text-secondary)] truncate inline-block max-w-[120px] align-middle"
                          title={c.db_ids.join(", ")}
                          data-testid={`db-ids-${c.campaign_id}`}
                        >
                          {c.db_ids.join(", ")}
                        </span>
                      ) : (
                        <span className="text-[var(--text-secondary)] text-xs">—</span>
                      )}
                    </td>
                    <td>
                      <VrStatusPill value={c.vr_status} url={c.vr_sheet_url} testid={`vr-status-${c.campaign_id}`} />
                    </td>
                    <td><SheetHealthDot health={c.sheet_health} /></td>
                    <td>
                      <button onClick={() => setCommentsFor(c)} className={`wl-btn-ghost text-xs inline-flex items-center gap-1 relative ${c.unread_comment_count ? "font-bold text-[var(--brand)]" : ""}`} data-testid={`cmts-${c.campaign_id}`} title={c.unread_comment_count ? `${c.unread_comment_count} new comment${c.unread_comment_count === 1 ? "" : "s"}` : "View comments"}>
                        <MessageCircle size={13} /> {c.comment_count || 0}
                        {c.unread_comment_count > 0 && (
                          <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-[var(--brand)] text-white" data-testid={`cmts-unread-${c.campaign_id}`}>{c.unread_comment_count}</span>
                        )}
                      </button>
                    </td>
                    <td className="text-right pr-6">
                      <div className="inline-flex items-center gap-1 relative">
                        <button onClick={() => setModal(c)} className="wl-btn-ghost text-xs inline-flex items-center gap-1" title="Edit" data-testid={`edit-${c.campaign_id}`}><Edit2 size={12} /> Edit</button>
                        <Link to={`/campaigns/${c.id}/tracking`} className="wl-btn-ghost text-xs inline-flex items-center gap-1" title="Open tracker" data-testid={`tracker-${c.campaign_id}`}><Eye size={12} /> Tracker</Link>
                        <Link to={`/admin/campaigns/${c.id}/config`} className="wl-btn-ghost text-xs inline-flex items-center" title="Configure dashboard" data-testid={`configure-${c.campaign_id}`}><BarChart3 size={12} /></Link>
                        <button className="wl-btn-ghost text-xs" onClick={() => setOpenMenu(openMenu === c.id ? null : c.id)} data-testid={`more-${c.campaign_id}`}><MoreHorizontal size={14} /></button>
                        {openMenu === c.id && (
                          <div className="absolute right-0 top-9 z-20 wl-card-solid shadow-lg w-48 py-1" onMouseLeave={() => setOpenMenu(null)}>
                            <MenuItem onClick={() => { onDuplicate(c.id); setOpenMenu(null); }} icon={Copy} testid={`menu-duplicate-${c.campaign_id}`}>Duplicate</MenuItem>
                            <MenuItem onClick={() => { setNotifyFor(c); setOpenMenu(null); }} icon={Mail} testid={`menu-notify-${c.campaign_id}`}>Notify brand…</MenuItem>
                            <MenuItem onClick={() => { onToggleVisibility(c); setOpenMenu(null); }} icon={c.visible_to_brand ? EyeOff : Eye} testid={`menu-visibility-${c.campaign_id}`}>{c.visible_to_brand ? "Hide from brand" : "Show to brand"}</MenuItem>
                            <MenuItem onClick={() => { onDeactivate(c); setOpenMenu(null); }} icon={Power} testid={`menu-deactivate-${c.campaign_id}`}>{c.status === "Active" ? "Deactivate" : "Activate"}</MenuItem>
                            <MenuItem onClick={() => { onDelete(c.id); setOpenMenu(null); }} icon={Trash2} destructive testid={`menu-delete-${c.campaign_id}`}>Delete</MenuItem>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal !== null && (
        <CampaignFormModal
          campaign={modal === "new" ? null : modal}
          brands={brands}
          teams={teams}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
          onConfigure={(cid) => { setModal(null); load(); nav(`/admin/campaigns/${cid}/config`); }}
        />
      )}

      {commentsFor && <CommentsDrawer campaign={commentsFor} onClose={() => { setCommentsFor(null); load(); }} onMarkedRead={load} />}
      {notifyFor && <NotifyBrandModal campaign={notifyFor} onClose={() => setNotifyFor(null)} />}
      </>
      )}

      {bulkEdit && (
        <CampaignBulkEditDialog
          campaignIds={bulkEdit.ids}
          campaigns={campaigns}
          contextLabel={bulkEdit.contextLabel}
          groups={groups}
          onClose={() => setBulkEdit(null)}
          onSaved={() => { setBulkEdit(null); setSelected(new Set()); load(); }}
        />
      )}
    </div>
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="wl-input w-auto pr-9" data-testid={testid}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function MenuItem({ children, onClick, icon: Icon, destructive, testid }) {
  return (
    <button onClick={onClick} className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-[var(--bg-page)] ${destructive ? "text-[var(--brand)]" : ""}`} data-testid={testid}>
      {Icon && <Icon size={14} />} {children}
    </button>
  );
}

// Small status chip for the Value Realisation pipeline state. Colour-codes the five
// allowed enum values so admins can scan the column at a glance. When a VR sheet URL
// is set, the pill becomes a link.
const VR_STATUS_STYLES = {
  not_generated:     { label: "Not gen.",   bg: "#f3f4f6", fg: "#6b7280" },
  in_progress:       { label: "In progress", bg: "#fef3c7", fg: "#92400e" },
  pending_review:    { label: "Review",     bg: "#dbeafe", fg: "#1e40af" },
  approved:          { label: "Approved",   bg: "#d1fae5", fg: "#065f46" },
  shared_with_brand: { label: "Shared",     bg: "#cffafe", fg: "#155e75" },
};

function VrStatusPill({ value, url, testid }) {
  const s = VR_STATUS_STYLES[value] || VR_STATUS_STYLES.not_generated;
  const chip = (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide whitespace-nowrap"
      style={{ background: s.bg, color: s.fg }}
      data-testid={testid}
    >
      {s.label}
    </span>
  );
  return url ? <a href={url} target="_blank" rel="noreferrer" title={url}>{chip}</a> : chip;
}
